import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useQuery, useMutation } from '@apollo/client';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Formik, Form } from 'formik';
import Page from '../../atoms/Page';
import Heading from '../../atoms/Heading';
import TierSettingsPanel from '../../organisms/TierSettingsPanel/TierSettingsPanel';
import LoadingSkeleton from '../../atoms/LoadingSkeleton';
import UnsavedChangesBar from '../../molecules/UnsavedChangesBar';
import './OrgTierSettingsPage.scss';
import {
  GET_NOTIFICATION_PLAN_AND_TIERS_GQL,
  GET_ORG_NOTIFICATION_SETTINGS_GQL,
  GET_BARN_OVERRIDES_GQL,
  UPSERT_ORG_FAULT_NOTIFICATION_TIER_SETTINGS_GQL,
  UPSERT_ORG_NOTIFICATION_PLAN_GQL,
} from '../OrgManagementPage/queries';
import BarnSettingsOverrideList from './BarnSettingsOverrideList';
import MessageHistoryLink from '../../molecules/MessageHistoryLink';
import { SelectSelfLoading } from '../../atoms/Select';
import { useCan, Privilege } from '@feedflo/access-control';
import * as Sentry from '@sentry/react';
import { alertHoursToThresholdSeconds, alertThresholdSecondsToHours } from '../../utils/tierNotificationSettings';

/**
 * Generates default tier settings structure from an array of tiers
 * @param {Array} tiers - Array of tier objects with shape: { id, sort_order, name, description }
 * @returns {Array} Array of tier settings with empty faultSettings, sorted by priority (descending)
 */
export function generateDefaultTierSettings(tiers) {
  if (!tiers || tiers.length === 0) {
    return [];
  }

  const settings = tiers.map((tier) => ({
    name: tier.name,
    description: tier.description,
    tierId: tier.id,
    priority: tier.sort_order,
    faultSettings: {},
  }));

  // Sort by priority (descending)
  settings.sort((a, b) => b.priority - a.priority);

  return settings;
}

function OrgTierSettingsPage({ titleSegments = [] }) {
  // const tierRef = useRef();
  // --- Route params ---
  const { id: organizationId } = useParams();

  // Check permissions
  const { can } = useCan();
  const canUpdateOrg = can('org', organizationId, Privilege.ORG_GENERAL_ADMINISTRATE);

  // --- State hooks ---
  const [isSaving, setIsSaving] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState(null);

  // --- Apollo/GraphQL hooks ---

  const {
    loading: savedPlanLoading,
    data: savedPlanData,
    error: savedPlanError,
  } = useQuery(GET_ORG_NOTIFICATION_SETTINGS_GQL, {
    variables: { org_id: organizationId },
    skip: !organizationId,
    // fetchPolicy: 'cache-first'
    fetchPolicy: 'network-only',
    context: { headers: { 'x-hasura-role': 'user' } },
  });

  const {
    loading: selectedPlanTiersLoading,
    data: selectedPlanTiersData,
    error: selectedPlanTiersError,
  } = useQuery(GET_NOTIFICATION_PLAN_AND_TIERS_GQL, {
    variables: {
      plan_id: selectedPlanId,
    },
    skip: !selectedPlanId,
    fetchPolicy: 'network-only',
    context: { headers: { 'x-hasura-role': 'user' } },
  });

  const savedPlanId = savedPlanData?.organization_notification_settings?.[0]?.plan_id ?? null;
  const initialPlanId = savedPlanData?.plans?.[0]?.plan_id;

  const [upsertOrgFaultSettings] = useMutation(UPSERT_ORG_FAULT_NOTIFICATION_TIER_SETTINGS_GQL, {
    context: { headers: { 'x-hasura-role': 'user' } },
    onError: (error) => {
      toast(`Error saving settings: ${error.message}`, { type: 'error' });
    },
  });

  const [upsertOrgNotificationPlan] = useMutation(UPSERT_ORG_NOTIFICATION_PLAN_GQL, {
    context: { headers: { 'x-hasura-role': 'user' } },
    onError: (error) => {
      toast(`Error saving notification plan: ${error.message}`, { type: 'error' });
    },
  });

  // --- Derived state ---
  // Extract tiers and their settings from the plan data
  const planTierCodeSettings = savedPlanData?.organization_notification_settings || [];

  // Transform the tier data into the format expected by TierSettingsPanel
  const transformedOrgSettings = useMemo(() => {
    const settings = {};

    // exit early if loading or error
    if (savedPlanLoading || selectedPlanTiersLoading || savedPlanError || selectedPlanTiersError) {
      return [];
    }

    if (selectedPlanId !== savedPlanId && selectedPlanTiersData?.plan?.tiers?.length > 0) {
      selectedPlanTiersData.plan.tiers
        .filter((tier) => tier.editable !== false)
        .forEach((tier) => {
          if (!settings[tier.id]) {
            settings[tier.id] = {
              name: tier.name,
              description: tier.description,
              tierId: tier.id,
              priority: tier.sort_order,
              faultSettings: {},
            };
          }

          tier?.default_fault_settings.forEach((faultSetting) => {
            // Add fault setting to the dictionary with fault code as key
            settings[tier.id].faultSettings[faultSetting.code.toString()] = {
              code: faultSetting.code,
              alert: {
                enabled: faultSetting.enabled,
                hours: alertThresholdSecondsToHours(faultSetting.alert_threshold_seconds, faultSetting.code),
                type: faultSetting.alert_threshold_type,
              },
              reminders: {
                enabled: faultSetting.renotify_enabled,
                hours: faultSetting.renotify_threshold_seconds / 3600,
              },
              resolved: {
                enabled: faultSetting.notify_on_resolved,
              },
            };
          });
        });
    } else if (!planTierCodeSettings || planTierCodeSettings.length === 0) {
      return [];
    } else {
      planTierCodeSettings
        .filter((faultSetting) => faultSetting.tier.editable !== false)
        .forEach((faultSetting) => {
          const tierId = faultSetting.tier.id;

          if (!settings[tierId]) {
            settings[tierId] = {
              name: faultSetting.tier.name,
              description: faultSetting.tier.description,
              tierId: tierId,
              priority: faultSetting.tier.sort_order,
              faultSettings: {},
            };
          }

          // Add fault setting to the dictionary with fault code as key
          settings[tierId].faultSettings[faultSetting.code.toString()] = {
            code: faultSetting.code,
            alert: {
              enabled: faultSetting.enabled,
              hours: alertThresholdSecondsToHours(faultSetting.alert_threshold_seconds, faultSetting.code),
              type: faultSetting.alert_threshold_type,
            },
            reminders: {
              enabled: faultSetting.renotify_enabled,
              hours: faultSetting.renotify_threshold_seconds / 3600,
            },
            resolved: {
              enabled: faultSetting.notify_on_resolved,
            },
          };
        });
    }

    // convert to array
    const settingsArray = Object.values(settings);
    // sort by priority
    settingsArray.sort((a, b) => b.priority - a.priority);
    return settingsArray;
  }, [planTierCodeSettings, selectedPlanTiersData, selectedPlanTiersLoading, selectedPlanTiersError]);

  // --- Navigation/side effects ---
  // Convert transformedOrgSettings to Formik-compatible format (object keyed by tierId)
  const initialTierSettingsFormValues = useMemo(() => {
    const formValues = {};
    transformedOrgSettings.forEach((setting) => {
      formValues[setting.tierId] = setting.faultSettings;
    });
    return formValues;
  }, [transformedOrgSettings]);

  // Initial form values including plan
  const initialFormValues = useMemo(
    () => ({
      planId: selectedPlanId ?? savedPlanId ?? initialPlanId ?? null,
      tierSettings: initialTierSettingsFormValues,
    }),
    [initialPlanId, initialTierSettingsFormValues, savedPlanId, selectedPlanId],
  );

  // --- Function definitions ---
  const handleFormSubmit = async (values) => {
    if (!organizationId) {
      toast('Organization ID is missing', { type: 'error' });
      return;
    }

    setIsSaving(true);
    try {
      const promises = [];

      // Save plan if it has changed
      if (values.planId && values.planId !== savedPlanId) {
        promises.push(
          upsertOrgNotificationPlan({
            variables: {
              org_id: organizationId,
              notification_tier_plan_id: values.planId,
            },
            refetchQueries: [GET_ORG_NOTIFICATION_SETTINGS_GQL, GET_BARN_OVERRIDES_GQL],
          }),
        );
      }

      // Save tier settings if they exist
      if (values.tierSettings && Object.keys(values.tierSettings).length > 0) {
        const allObjects = [];
        Object.entries(values.tierSettings).forEach(([tierId, faultSettings]) => {
          if (!faultSettings || typeof faultSettings !== 'object') return;
          const objects = Object.entries(faultSettings).map(([code, setting]) => ({
            notification_tier_id: tierId,
            code: parseInt(code),
            enabled: setting.alert?.enabled,
            alert_threshold_seconds: alertHoursToThresholdSeconds(setting.alert?.hours, code),
            alert_threshold_type: setting.alert?.type,
            renotify_enabled: setting.reminders?.enabled,
            renotify_threshold_seconds: setting.reminders?.hours ? setting.reminders.hours * 3600 : 0,
            notify_on_resolved: setting.resolved?.enabled,
            organization_id: organizationId,
          }));
          allObjects.push(...objects);
        });

        if (allObjects.length > 0) {
          promises.push(
            upsertOrgFaultSettings({
              variables: { objects: allObjects },
              refetchQueries: [GET_ORG_NOTIFICATION_SETTINGS_GQL],
            }),
          );
        }
      }

      // Execute all saves in parallel
      await Promise.all(promises);

      if (promises.length > 0) {
        setSelectedPlanId(values.planId);
        toast('Organization notification policy saved', {
          type: 'success',
          position: 'top-right',
          autoClose: 3000,
          closeOnClick: true,
        });
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      Sentry.captureException(error);
      toast(`Error saving settings: ${error.message}`, { type: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  // a separate render function to keep things readable but include a nicer loading skeleton
  const renderTierSettingsContent = (values, setFieldValue) => {
    if (selectedPlanTiersLoading) {
      return (
        <>
          <div className="OrgTierSettingsPage-tierItem">
            <LoadingSkeleton style={{ width: '100%', height: '300px' }} />
          </div>
          <div className="OrgTierSettingsPage-tierItem">
            <LoadingSkeleton style={{ width: '100%', height: '300px' }} />
          </div>
        </>
      );
    }

    if (values.planId !== '' && transformedOrgSettings?.length > 0) {
      return transformedOrgSettings.map((settings) => (
        <div key={settings.tierId} className="OrgTierSettingsPage-tierItem">
          <TierSettingsPanel
            tierName={settings.name}
            tierDescription={settings.description}
            settings={values.tierSettings?.[settings.tierId] || settings.faultSettings || {}}
            onSettingChange={(newFaultSettings) => {
              setFieldValue(`tierSettings.${settings.tierId}`, newFaultSettings, true);
            }}
            disabled={false}
            showTitle={true}
          />
        </div>
      ));
    }

    return (
      <>
        <div className="OrgTierSettingsPage-noPlan">
          <p>No notification policies found for this plan. Contact your admin or support.</p>
        </div>
        {/* Barn-Specific Overrides - show even when no plan selected */}
        <BarnSettingsOverrideList
          organizationId={organizationId}
          planId={savedPlanId}
          orgTierSettings={transformedOrgSettings}
        />
      </>
    );
  };

  const pageTitleSegments = useMemo(() => ['Organization Tier Settings', ...titleSegments], []);

  // Show loading state
  if (savedPlanLoading) {
    return (
      <Page className="OrgTierSettingsPage" titleSegments={pageTitleSegments}>
        <div className="OrgTierSettingsPage-content">
          <LoadingSkeleton style={{ width: '100%', height: '400px' }} />
        </div>
      </Page>
    );
  }

  // Show error state
  // TODO: replace this with an error boundary
  if (savedPlanError || selectedPlanTiersError) {
    return (
      <Page className="OrgTierSettingsPage" titleSegments={pageTitleSegments}>
        <div className="OrgTierSettingsPage-content">
          <div>Error loading data: {savedPlanError?.message}</div>
        </div>
      </Page>
    );
  }

  // Check if user can see page
  if (!canUpdateOrg) {
    return (
      <Page className="OrgTierSettingsPage" titleSegments={pageTitleSegments}>
        <div className="OrgTierSettingsPage-content">
          <div>You do not have permission to access this page.</div>
        </div>
      </Page>
    );
  }

  console.log('plan ids', savedPlanId, selectedPlanId);
  // --- Render ---
  return (
    <Page className="OrgTierSettingsPage" titleSegments={pageTitleSegments}>
      <div className="OrgTierSettingsPage-content">
        <div className="OrgTierSettingsPage-section">
          <div className="OrgTierSettingsPage-card">
            <div className="OrgTierSettingsPage-cardHeader">
              <div className="OrgTierSettingsPage-headerInfo">
                <Heading className="OrgTierSettingsPage-sectionTitle">Organization-Wide Notification Settings</Heading>
                <MessageHistoryLink filters={{ orgId: organizationId }} />
                <div className="OrgTierSettingsPage-headerDescription">
                  Configure the default notification policy for your organization. These settings apply to all barns
                  unless a barn has its own override.
                </div>
              </div>
            </div>

            <div className="OrgTierSettingsPage-cardContent">
              <Formik initialValues={initialFormValues} enableReinitialize onSubmit={handleFormSubmit}>
                {({ values, setFieldValue, dirty, handleSubmit, isValid }) => (
                  <>
                    <Form>
                      {(dirty || values.planId !== savedPlanId) && (
                        <UnsavedChangesBar isValid={isValid} loading={isSaving} onSaveClick={handleSubmit} />
                      )}
                      <div className="OrgTierSettingsPage-planSelector">
                        <div className="OrgTierSettingsPage-planSelectorContent">
                          <SelectSelfLoading
                            label="Select Notification Plan"
                            defaultText={'Select a Plan'}
                            defaultTextDisabled={true}
                            fieldTableName={'notification_plan'}
                            onChange={(x) => {
                              setSelectedPlanId(x.target.value);
                              setFieldValue('planId', x.target.value);
                            }}
                            // yes this looks weird, but the Select needs undefined to pick the default value
                            value={values.planId ?? undefined}
                          />
                        </div>
                      </div>

                      <Heading className="OrgTierSettingsPage-settingsTitle">Notification Policies</Heading>
                      <div className="OrgTierSettingsPage-tiersList">
                        {renderTierSettingsContent(values, setFieldValue)}
                      </div>
                    </Form>
                    {/* Barn-Specific Overrides - use Formik value for planId */}
                    <BarnSettingsOverrideList
                      organizationId={organizationId}
                      planId={values.savedPlan || savedPlanId}
                      orgTierSettings={transformedOrgSettings}
                    />
                  </>
                )}
              </Formik>
            </div>
          </div>
        </div>
      </div>
    </Page>
  );
}

OrgTierSettingsPage.propTypes = {
  titleSegments: PropTypes.arrayOf(PropTypes.string),
};

export default OrgTierSettingsPage;
